import { FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import "./right-to-know.css";
import { ExternalLink } from "../../../../../../common/components/ExternalLink";
import { InlineLoading } from "../../../../../../common/components/Loading";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { usePolling } from "../../../../../../common/hooks/useInterval";
import { Api } from "../../../../../../common/service/Api";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import { VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM } from "../../../../../types/Vendor";
import { sortVendorsByCategory } from "../../../../../util/vendorCategorization";
import { Callout, CalloutVariant } from "../../../../Callout";
import { DataRecipientLogo } from "../../../../DataRecipientLogo";
import { AUTOMATION_OUTCOMES, AutomationOutcomeRow } from "../shared/AutomationOutcomeRow";
import {
  ConnectedSystemAutomationCallout,
  isAwaitingConnectedSystemAutomation,
  useConnectedSystemAutomationBlock,
} from "../shared/ConnectedSystemAutomationCallout";
import { NestedVendorList } from "../shared/NestedVendorList";
import { CategorizedVendorList } from "../shared/CategorizedVendorList";
import { CategoryHeader } from "../shared/CategoryHeader";
import { InstructionsButton, InstructionsContent } from "../shared/CollapsibleInstructions";
import { useInstructionsState } from "../shared/useInstructionsState";
import { SensitiveInfoCallout } from "../../../../dialog/SensitiveInfoDialog";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import {
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
  isVendorDataAccessibleForWorkflow,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { getVendorNesting } from "../../../../../util/vendorNesting";
import { useDataRetrievedVia } from "../../../../../hooks/useManagedAdNetworks";
import { useDataRetrievedViaHintDismissal } from "../../../../../hooks/useDataRetrievedViaHintDismissal";
import { Routes } from "../../../../../root/routes";
import { useDataMapCollectedPIC } from "../../../../../../common/service/resources/data-map/dataMapTransformers";
import { PersonalIdentifiersData, PersonalIdentifiersForm } from "./PersonalIdentifiersForm";
import { StickyFileDropper } from "../shared/StickyFileDropper";
import { useDataRequestAttachments } from "../../../../../hooks/useDataRequestAttachments";
import { AlertCard } from "@truevault/aquilo";
import { NetworkRequest } from "../../../../../../common/models";
import { useAvailableIntegrationVendorIds } from "../../../../../hooks/useAvailableIntegrationVendorIds";

const normalizePersonalIdentifiers = (data: PersonalIdentifiersData): PersonalIdentifiersData => ({
  firstName: (data.firstName || "").trim(),
  lastName: (data.lastName || "").trim(),
  email: (data.email || "").trim(),
  phone: (data.phone || "").trim(),
  subjectFreePhone: (data.subjectFreePhone || "").trim(),
  postalAddress: (data.postalAddress || "").trim(),
  subjectFreeTextAddress: (data.subjectFreeTextAddress || "").trim(),
  onlineIdentifiers: (data.onlineIdentifiers || "").trim(),
  customFields: data.customFields || [],
});

// Helper to extract custom fields from request
const extractCustomFieldsFromRequest = (request: any): Array<{ label: string; value: string }> => {
  const customFields: Array<{ label: string; value: string }> = [];
  const excludedKeys = new Set([
    "address1",
    "address2",
    "city",
    "state",
    "zipCode",
    "country",
    "phoneNumber",
  ]);

  // Check for legacy organizationCustomInput field
  if (request.organizationCustomInput) {
    customFields.push({
      label: "Custom Field",
      value: request.organizationCustomInput,
    });
  }

  // Add custom fields from subjectCustomFieldsFmt
  if (request.subjectCustomFieldsFmt) {
    Object.entries(request.subjectCustomFieldsFmt).forEach(([label, value]) => {
      const keyToCheck = label
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[()]/g, "")
        .replace(/line1/g, "1")
        .replace(/line2/g, "2");

      const isExcluded =
        excludedKeys.has(keyToCheck) ||
        label.toLowerCase().includes("address") ||
        label.toLowerCase().includes("city") ||
        label.toLowerCase().includes("state") ||
        label.toLowerCase().includes("zip") ||
        label.toLowerCase().includes("country") ||
        label.toLowerCase().includes("phone");

      if (!isExcluded && value) {
        customFields.push({ label, value: String(value) });
      }
    });
  }

  return customFields;
};

export const RetrieveInfo = () => {
  const [org] = usePrimaryOrganization();

  const [allVendors, vendorsRequest] = useOrganizationVendors(org.id, undefined, true);
  const { integrationAvailability, privacyCenterRequest, integrationAvailabilityRequest } =
    useAvailableIntegrationVendorIds();

  const {
    request,
    refreshRequest,
    setStepCompleted,
    setUpdateRequests,
    setPersonalIdentifiers,
    isGeneratingAccessZip,
  } = useDataRequestProcessingContext();
  const isPortabilityRequest = request.dataRequestType?.workflowType === "PORTABILITY";

  const { attachments, uploadFile, deleteFile, isUploading } = useDataRequestAttachments(
    org.id,
    request.id,
  );
  const [vendorOutcomes, setVendorOutcomes] = useState<Record<string, string>>({});
  const instructionsState = useInstructionsState();
  const [customIdentifierRowCount, setCustomIdentifierRowCount] = useState<number>(0);

  const latestPersistedIdentifiersRef = useRef<PersonalIdentifiersData | null>(null);

  const { data: dataMap } = useDataMap(org.id);

  const { fetch: updateVendorOutcome, request: updateOutcomeRequest } = useActionRequest({
    api: async ({ values, resetState }) => {
      try {
        const resp = await Api.dataRequest.updateVendor(org.id, request.id, values);
        if (resp.id == undefined) {
          resetState();
        }
        refreshRequest();
      } catch (err) {
        resetState();
        throw err;
      }
    },
    allowConcurrent: true,
    onError: refreshRequest,
  });

  const onSelectionChange = (vendor, value) => {
    const resetState = () =>
      setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.id]: vendorOutcomes }));

    setVendorOutcomes((prev) => Object.assign({}, prev, { [vendor.vendorId]: value }));
    updateVendorOutcome({
      values: { vendor: vendor.vendorId, outcome: value },
      resetState: resetState,
    });
  };

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);
  const { data: dataRetrievedVia, isError: dataRetrievedViaFailed } = useDataRetrievedVia(org.id);
  const { dismissed: dataRetrievedViaHintDismissed, dismiss: dismissDataRetrievedViaHint } =
    useDataRetrievedViaHintDismissal(org.id);

  const requestProcessingRegion = getRequestProcessingRegion(request);
  // Wait for the "Data retrieved via" links too, so a linked vendor doesn't flash in as its own row first. If they
  // fail to load, every vendor simply keeps its own row.
  const integrationsReady =
    NetworkRequest.areFinished(
      vendorsRequest,
      privacyCenterRequest,
      integrationAvailabilityRequest,
    ) &&
    (dataRetrievedVia !== undefined || dataRetrievedViaFailed);

  /**
   * Whether a recipient belongs in this step on its own merits, ignoring vendor nesting.
   */
  const belongsInRetrieveInfoStep = useCallback(
    (v: OrganizationDataRecipientDto) => {
      const matchesProcessingRegion = v.processingRegions.includes(requestProcessingRegion);
      const instruction = v.instructions.find((vi) => vi.requestType == "KNOW");
      const instructionOk = instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED";
      const contextOk = isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context,
        v,
        request,
        org,
      );
      return (
        matchesProcessingRegion &&
        isVendorDataAccessibleForWorkflow(v, integrationAvailability) &&
        instructionOk &&
        contextOk
      );
    },
    [dataMap, integrationAvailability, org, request, requestProcessingRegion],
  );

  // Recipients handled by a managing vendor (an ad-network manager, or TrueVault Connected System) are listed under
  // that manager instead of getting their own outcome row (POL-7958) - every recipient this step would have shown.
  // So are recipients whose data is retrieved via another recipient (DES-43), which only applies to this step.
  const nesting = useMemo(
    () =>
      getVendorNesting(allVendors, managedAdNetworks, answers, {
        isNestedVendorRelevant: belongsInRetrieveInfoStep,
        dataRetrievedVia,
        // Links are org-wide, but a vendor only stands in for another on requests it would be listed for itself:
        // same region and context, with accessible data.
        isDataRetrievedViaEligible: belongsInRetrieveInfoStep,
      }),
    [allVendors, managedAdNetworks, answers, belongsInRetrieveInfoStep, dataRetrievedVia],
  );

  const vendors = useMemo(() => {
    if (!integrationsReady) return undefined;

    const result = allVendors?.filter((v) => {
      if (nesting.isManaged(v.id)) return false;

      // The Connected System access handoff renders as a read-only row in the Automations
      // section below instead of a standard outcome row (POL-7757).
      const isConnectedSystemAutomationRow =
        v.vendorKey === VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM &&
        request.vendorOutcomes.some(
          (vo) => vo.vendor.id === v.vendorId && AUTOMATION_OUTCOMES.includes(vo.outcome),
        );
      if (isConnectedSystemAutomationRow) return false;

      return belongsInRetrieveInfoStep(v);
    });

    if (!result) {
      return result;
    }

    // Managers stand in for the recipients they handle, so a manager that manages anything shown in this step gets a
    // row even when it would not qualify on its own. The Connected System is the exception: while its access handoff
    // has an outcome it belongs in the Automations section, which carries its own nested list.
    const managersToAdd = nesting.activeManagers.filter(
      (manager) =>
        !result.find((v) => v.id === manager.id) &&
        !request.vendorOutcomes.some(
          (vo) => vo.vendor.id === manager.vendorId && AUTOMATION_OUTCOMES.includes(vo.outcome),
        ),
    );

    return result.concat(managersToAdd);
  }, [allVendors, belongsInRetrieveInfoStep, integrationsReady, nesting, request]);

  // set the update requests
  useEffect(() => {
    setUpdateRequests(updateOutcomeRequest);
  }, [updateOutcomeRequest]);

  // Automation outcomes recorded on the request whose vendor is not shown as an outcome row above.
  // The Connected System access handoff row always renders here; other automation outcomes (e.g. a
  // lookup vendor's "no data" result) surface here only when their vendor was filtered out of the
  // list, mirroring the Delete and Opt-Out steps.
  const automationOnlyOutcomes = useMemo(() => {
    const shownVendorIds = new Set(vendors?.map((v) => v.vendorId) ?? []);
    return request.vendorOutcomes.filter(
      (outcome) =>
        AUTOMATION_OUTCOMES.includes(outcome.outcome) && !shownVendorIds.has(outcome.vendor.id),
    );
  }, [request.vendorOutcomes, vendors]);

  // While the Connected System handoff is awaiting the vendor's response, the request cannot move
  // to Send Data; poll so the resolved outcome appears without a manual reload. The outcome radio
  // rows keep their own optimistic local state, so a background refresh won't clobber user edits.
  const hasWaitingAutomation = useMemo(
    () => request.vendorOutcomes.some((vo) => vo.outcome === "AUTOMATION_WAITING"),
    [request.vendorOutcomes],
  );
  usePolling(refreshRequest, 5000, hasWaitingAutomation);

  const retryAccessAutomation = useCallback(async () => {
    await Api.dataRequest.manuallyAutomateRequest(org.id, request.id);
    refreshRequest();
  }, [org.id, request.id, refreshRequest]);

  useEffect(() => {
    const hasVendors = Boolean(vendors);
    const everyVendorHasOutcome = vendors?.every((v) => {
      const outcome = request.vendorOutcomes.find((vo) => vo.vendor.id == v.vendorId);
      return outcome && outcome.outcome != null;
    });

    setStepCompleted(
      integrationsReady && hasVendors && everyVendorHasOutcome && !hasWaitingAutomation,
    );
  }, [request, setStepCompleted, vendors, hasWaitingAutomation, integrationsReady]);

  useEffect(() => {
    const vendorOutcomes = request.vendorOutcomes.reduce((acc, outcome) => {
      return Object.assign({}, acc, { [outcome.vendor.id]: outcome.outcome });
    }, {});
    setVendorOutcomes((prev) => Object.assign({}, prev, vendorOutcomes));
  }, [request, vendors]);

  const sortedVendors = useMemo(() => {
    return sortVendorsByCategory(vendors ?? []);
  }, [vendors]);

  // The Connected System row has no manual controls, so the step waits on its access handoff (POL-7958).
  const stepAwaitsConnectedSystem = (vendors ?? []).some((vendor) =>
    isAwaitingConnectedSystemAutomation(vendor, vendorOutcomes[vendor.vendorId], request),
  );
  useConnectedSystemAutomationBlock(stepAwaitsConnectedSystem, request.allowManualAutomation);

  // Get collected PICs from consumer collection groups only (exclude HR groups)
  const collectedPICs = useDataMapCollectedPIC(dataMap, {
    collectionGroupType: ["BUSINESS_TO_CONSUMER"],
  });

  useEffect(() => {
    latestPersistedIdentifiersRef.current = normalizePersonalIdentifiers({
      firstName: request.subjectFirstName || "",
      lastName: request.subjectLastName || "",
      email: request.subjectEmailAddress || "",
      phone: request.subjectPhoneNumber || "",
      subjectFreePhone: request.subjectFreePhone || "",
      postalAddress: request.subjectMailingAddress || "",
      subjectFreeTextAddress: request.subjectFreeTextAddress || "",
      onlineIdentifiers: latestPersistedIdentifiersRef.current?.onlineIdentifiers || "",
      customFields: extractCustomFieldsFromRequest(request),
    });
  }, [
    request.subjectFirstName,
    request.subjectLastName,
    request.subjectEmailAddress,
    request.subjectPhoneNumber,
    request.subjectFreePhone,
    request.subjectMailingAddress,
    request.subjectFreeTextAddress,
    request.subjectCustomFieldsFmt,
    request.organizationCustomInput,
  ]);

  const persistPersonalIdentifiers = useCallback(
    async (data: PersonalIdentifiersData) => {
      const normalized = normalizePersonalIdentifiers(data);
      const previous = latestPersistedIdentifiersRef.current;

      latestPersistedIdentifiersRef.current = normalized;

      try {
        await Api.dataRequest.updateRequest(org.id, request.id, {
          requestDate: request.requestDate,
          subjectFirstName: normalized.firstName,
          subjectLastName: normalized.lastName,
          // Email is read-only here; client form state must not change the request recipient.
          subjectEmailAddress: request.subjectEmailAddress,
          // Only save to "free" fields to prevent retroactive locking
          // subjectPhoneNumber and subjectMailingAddress should remain unchanged (they hold original pulled-in data)
          subjectFreePhone: normalized.phone,
          subjectFreeTextAddress: normalized.postalAddress,
          subjectOnlineIdentifiers: normalized.onlineIdentifiers,
          submissionMethod: request.submissionMethod || "MANUAL",
          dataRequestType: request.dataRequestType,
        });
        refreshRequest();
      } catch (error) {
        latestPersistedIdentifiersRef.current = previous;
        console.error("[RetrieveInfo] Failed to save personal identifiers:", error);
      }
    },
    [
      org.id,
      request.id,
      request.requestDate,
      request.subjectEmailAddress,
      request.submissionMethod,
      request.dataRequestType,
      refreshRequest,
    ],
  );

  const handlePersonalIdentifiersBlur = useCallback(
    (data: PersonalIdentifiersData) => {
      setPersonalIdentifiers(data);
      void persistPersonalIdentifiers(data);
    },
    [setPersonalIdentifiers, persistPersonalIdentifiers],
  );

  if (!integrationsReady) {
    return null;
  }

  return (
    <>
      <div className="text-t3 mb-std">
        {isPortabilityRequest ? (
          <>
            Download files containing the consumer&apos;s personal data from your Data Recipients
            below that provide data in a portable format, such as a CSV or PDF. For any systems that
            do not offer a way to download the consumer&apos;s data in a portable document, select
            "No data to retrieve."
          </>
        ) : (
          <>
            Retrieve specific personal information you have about the consumer. Attach data files or
            type in data collected below. Your defaults and instructions can be updated in Request
            Instructions.
          </>
        )}
      </div>
      {isPortabilityRequest && (
        <Callout
          collapsible
          title={<span className="text-t2">What is the Right to Portability?</span>}
          variant={CalloutVariant.Clear}
        >
          The right to data portability allows individuals to obtain and reuse their personal data
          for their own purposes across different services. It allows them to move, copy or transfer
          personal data easily from one IT environment to another in a safe and secure way, without
          affecting its usability.
        </Callout>
      )}
      <div className="mb-std flex flex-col gap-3">
        <SensitiveInfoCallout collectedPICs={collectedPICs} />
        <div className="tw-boundary">
          <AlertCard variant="info" containerClasses="mt-0 mb-0">
            <div className="text-t3">
              <strong>Remember to include inferences.</strong> If applicable, include any inferences
              your organization has made about this consumer (e.g., predicted preferences, scores,
              or categories derived from their data).
            </div>
          </AlertCard>
        </div>
      </div>
      <div className="mb-std flex flex-row gap-4 items-stretch">
        <div className="flex-1">
          <Callout variant={CalloutVariant.WhiteDropShadow}>
            <div className="know-section-title">Consumer's Collected Data</div>
            <div className="know-section-subtitle">
              These are Personal Identifiers your business collects.
            </div>
            <PersonalIdentifiersForm
              request={request}
              organization={org}
              onChange={handlePersonalIdentifiersBlur}
              onCustomIdentifierRowCountChange={setCustomIdentifierRowCount}
            />
          </Callout>
        </div>

        {/* File Upload Section */}
        <div className="flex-1 flex flex-col">
          <StickyFileDropper
            onFileUpload={uploadFile}
            uploadedFiles={attachments.map((att) => ({
              id: att.id,
              filename: att.filename,
              size: att.size,
              status: att.status,
              uploadedAt: att.uploadedAt,
              url: att.url,
            }))}
            onFileDelete={deleteFile}
            // Lock uploads and deletes while the zip is built from the current attachment list
            disabled={isUploading || isGeneratingAccessZip}
            customIdentifierRowCount={customIdentifierRowCount}
          />
        </div>
      </div>

      {dataRetrievedVia?.length === 0 && dataRetrievedViaHintDismissed === false && (
        <div className="tw-boundary mb-std">
          <AlertCard variant="info" containerClasses="mt-0 mb-0">
            <div className="flex items-start gap-3">
              <div className="text-t3 flex-1">
                If you retrieve a vendor&apos;s data from another vendor (for example, shipping data
                from Shopify for a vendor like FedEx), you can set its{" "}
                <strong>Data retrieved via</strong> in Access workflow setup. It&apos;ll then be
                listed under that vendor here, so you answer once for both.{" "}
                <Link
                  className="text-blue-600 hover:underline font-medium"
                  to={
                    request.context === "EMPLOYEE"
                      ? `${Routes.hrCenter.hrDsarWorkflows}?view=access`
                      : Routes.organization.requestHandling.accessRequest
                  }
                >
                  Go to Access setup
                </Link>
              </div>
              <button
                type="button"
                aria-label="Dismiss Data retrieved via hint"
                className="flex items-center bg-transparent p-0 cursor-pointer text-blue-600"
                style={{ border: 0 }}
                onClick={dismissDataRetrievedViaHint}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </AlertCard>
        </div>
      )}

      {sortedVendors && (
        <CategorizedVendorList
          vendors={sortedVendors}
          renderVendor={(vendor, isLast, isFirstInCategory) => {
            const instruction = vendor.instructions.find((vi) => vi.requestType == "KNOW");

            const localOutcome = vendorOutcomes[vendor.vendorId] || "";
            const remoteOutcome =
              request.vendorOutcomes.find((vo) => vo.vendor.id == vendor.vendorId)?.outcome || "";

            // The Connected System is resolved by its access handoff automation, so it gets an
            // explanation instead of manual outcome radios (POL-7958).
            const awaitingConnectedSystem = isAwaitingConnectedSystemAutomation(
              vendor,
              localOutcome,
              request,
            );

            const nestedVendorList = (
              <>
                <NestedVendorList
                  managerName={vendor.name}
                  nestedVendors={nesting.getManagedVendorsForManager(vendor.id)}
                />
                <NestedVendorList
                  managerName={vendor.name}
                  nestedVendors={nesting.getRetrievedViaVendorsForManager(vendor.id)}
                  title="Also includes data from:"
                />
              </>
            );

            // The awaiting callout gets the vendor name centered on it however many lines either wraps to; the nested
            // list wraps onto a second line so it never stretches the line the two share (POL-7992).
            return (
              <div key={vendor.id}>
                {!isFirstInCategory && <hr />}
                <div
                  key={`vendor-${vendor.id}`}
                  className={clsx("flex-container pt-std pb-std border-top--grey", {
                    "flex--wrap flex--align-center": awaitingConnectedSystem,
                  })}
                >
                  <div className="text-t4 w-256 flex-container flex--align-start pl-xs">
                    <DataRecipientLogo
                      className="w-space-large align-top mr-mdlg"
                      dataRecipient={vendor}
                    />
                    <div>
                      <span>{vendor.name}</span>
                      {!awaitingConnectedSystem && nestedVendorList}
                    </div>
                  </div>
                  <div className={awaitingConnectedSystem ? "flex--expand-self" : "flex-grow"}>
                    {awaitingConnectedSystem ? (
                      <ConnectedSystemAutomationCallout />
                    ) : (
                      <FormControl component="fieldset" className={"mb-md"}>
                        <RadioGroup
                          aria-label="Vendor handling response"
                          className="align-items-center know-radio-group"
                          name={`vendor-${vendor.id}-data-handling`}
                          onChange={(e) => {
                            onSelectionChange(vendor, e.target.value);
                          }}
                          value={localOutcome}
                          row
                        >
                          <FormControlLabel
                            value="NO_DATA"
                            control={<Radio />}
                            label={
                              <span className="text-t2 pr-xs know-radio-label">
                                No data to retrieve
                              </span>
                            }
                            className={clsx({
                              highlight:
                                instruction?.processingMethod == "INACCESSIBLE_OR_NOT_STORED",
                            })}
                          />
                          <FormControlLabel
                            control={<Radio />}
                            value="DATA_RETRIEVED"
                            label={
                              <span className="text-t2 pr-xs know-radio-label">Data retrieved</span>
                            }
                            className={clsx({
                              highlight:
                                instruction?.processingMethod != "INACCESSIBLE_OR_NOT_STORED",
                            })}
                          />
                          <FormControlLabel
                            control={<Radio />}
                            value="DATA_IN_FORM"
                            label={
                              <span className="text-t2 pr-xs know-radio-label">
                                Data in form (above)
                              </span>
                            }
                          />

                          <InstructionsButton
                            vendorId={vendor.vendorId}
                            hasInstructions={Boolean(
                              vendor.accessInstructions || instruction?.processingInstructions,
                            )}
                            isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                            onToggle={instructionsState.toggleInstructions}
                          />

                          {localOutcome != remoteOutcome && (
                            <div className="know-loading-indicator">
                              <InlineLoading />
                            </div>
                          )}
                        </RadioGroup>
                      </FormControl>
                    )}

                    {!awaitingConnectedSystem && vendor.ccpaRequestToKnowLink && (
                      <Callout
                        variant={CalloutVariant.Gray}
                        title="Access Instructions"
                        className="mb-md"
                      >
                        <ExternalLink
                          href={vendor.ccpaRequestToKnowLink}
                          target="_blank"
                          className="text-style-normal"
                        >
                          {vendor.name} Instructions
                        </ExternalLink>
                      </Callout>
                    )}
                  </div>
                  {awaitingConnectedSystem && (
                    <div className="w-100">
                      <div className="w-256">{nestedVendorList}</div>
                    </div>
                  )}
                </div>

                {/* Instructions content appears below the vendor row */}
                <InstructionsContent
                  instructions={vendor.accessInstructions}
                  processingInstructions={instruction?.processingInstructions}
                  title="Access Instructions"
                  isExpanded={instructionsState.isExpanded(vendor.vendorId)}
                />
              </div>
            );
          }}
        />
      )}

      {automationOnlyOutcomes.length > 0 && (
        <>
          <CategoryHeader categoryName="Automations" showVendorsSuffix={false} />

          {automationOnlyOutcomes.map((outcome) => (
            <AutomationOutcomeRow
              key={outcome.vendor.id}
              vendor={outcome.vendor}
              outcome={outcome.outcome}
              variant="access"
              nestedVendors={nesting.getManagedVendorsForManager(
                allVendors?.find((v) => v.vendorId === outcome.vendor.id)?.id,
              )}
              retrievedViaVendors={nesting.getRetrievedViaVendorsForManager(
                allVendors?.find((v) => v.vendorId === outcome.vendor.id)?.id,
              )}
              onRetry={
                outcome.vendor.vendorKey === VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM
                  ? retryAccessAutomation
                  : undefined
              }
            />
          ))}
        </>
      )}
    </>
  );
};
